import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';

// app
import { Avatar } from 'components';
import styles from './Info.styles';

// mui
import { makeStyles, Typography } from '@material-ui/core';

const avatarSizeMap = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
  xl: 64,
};

InfoView.propTypes = {
  avatarText: PropTypes.string,
  avatarImage: PropTypes.string,
  avatarIcon: PropTypes.object,
  avatarBg: PropTypes.string,
  avatarBorder: PropTypes.bool,
  avatarSize: PropTypes.number,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  title: PropTypes.node,
  content: PropTypes.node,
  subtitle: PropTypes.node,
  description: PropTypes.node,
  ellipsis: PropTypes.bool,
  verticalAlign: PropTypes.bool,
  nestedClasses: PropTypes.shape({
    root: PropTypes.string,
    avatar: PropTypes.string,
    details: PropTypes.string,
    content: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
  }),
  'data-testid': PropTypes.string,
  link: PropTypes.string,
};

export function InfoView({
  avatarText,
  avatarImage,
  avatarIcon,
  avatarBg,
  avatarBorder,
  avatarSize,
  size,
  title,
  subtitle,
  content,
  description,
  ellipsis,
  verticalAlign,
  nestedClasses,
  link,
  handleLinkClick,
  dataTestId,
}) {
  const classes = makeStyles(styles, { name: 'Info' })();

  const verticalAlignClasses = {
    [classes.singleLine]: (!description && !isNumber(description) && !content) || verticalAlign,
  };

  const rootClasses = {
    [classes.ellipsis]: ellipsis,
  };

  const contentClasses = {
    [classes.ellipsis]: ellipsis,
  };

  const avatarClasses = {
    [classes.avatarXs]: size === 'xs',
    [classes.avatarSm]: size === 'sm',
    [classes.avatarMd]: size === 'md',
    [classes.avatarLg]: size === 'lg',
    [classes.avatarXl]: size === 'xl',
  };

  const textClasses = {
    [classes.textFontSizeXs]: size === 'xs',
    [classes.textFontSizeSm]: size === 'sm',
    [classes.textFontSizeMd]: size === 'md',
    [classes.textFontSizeLg]: size === 'lg',
    [classes.textFontSizeXl]: size === 'xl',
  };

  const subtitleClasses = {
    [classes.subtitleFontSizeXs]: size === 'xs',
    [classes.subtitleFontSizeSm]: size === 'sm',
    [classes.subtitleFontSizeMd]: size === 'md',
    [classes.subtitleFontSizeLg]: size === 'lg',
    [classes.subtitleFontSizeXl]: size === 'xl',
  };

  return (
    <div className={classnames(classes.root, rootClasses, nestedClasses.root)} data-testid={`info-${dataTestId}`}>
      <Avatar
        onClick={handleLinkClick}
        size={avatarSize || avatarSizeMap[size]}
        text={avatarText ? avatarText.substring(0, 2) : undefined}
        icon={avatarIcon}
        src={avatarImage}
        bg={avatarBg}
        border={typeof avatarBorder === 'boolean' ? avatarBorder : !!avatarIcon}
        avatarClasses={classnames(classes.avatar, avatarClasses, nestedClasses.avatar, { [classes.isLink]: !!link })}
        data-testid={`info-${dataTestId}-avatar`}
      />

      {(title || subtitle || description || isNumber(description)) && (
        <div
          className={classnames(classes.content, verticalAlignClasses, contentClasses, nestedClasses.details)}
          data-testid={`info-${dataTestId}-content`}
        >
          <Typography
            variant="body2"
            noWrap={ellipsis}
            title={isString(title) ? title : null}
            className={classnames(classes.title, textClasses, nestedClasses.title)}
            data-testid={`info-${dataTestId}-title`}
          >
            <span className={classnames({ [classes.isLink]: !!link })} onClick={handleLinkClick}>
              {title}
            </span>
            {subtitle && (
              <Typography
                variant="body2"
                component="span"
                title={isString(subtitle) ? subtitle : null}
                className={classnames(classes.subtitle, subtitleClasses)}
                data-testid={`info-${dataTestId}-subtitle`}
              >
                {subtitle}
              </Typography>
            )}
          </Typography>

          {(description || isNumber(description)) && (
            <Typography
              variant="body2"
              noWrap={ellipsis}
              title={isString(description) ? description : null}
              className={classnames(classes.description, textClasses, nestedClasses.description)}
              data-testid={`info-${dataTestId}-data`}
            >
              {description}
            </Typography>
          )}

          {content && <div className={nestedClasses.content}>{content}</div>}
        </div>
      )}
    </div>
  );
}
